// ./src/utils/eventFeaturePlans.ts
//
// "Which plans include this feature?" — for the premium note on an event-feature card (ROADMAP A2b #62, owner
// 2026-09-29: show premium features, mark them, and name the plans that have them — "some features are available in
// one plan or another").
//
// Derived from `appPlanList` (the plans the Subscription page already loads): a plan includes an event feature when
// its value for the feature's PLAN code is true. Every plan code an event feature points at is a yes/no feature.
// ⚠ Only ACTIVE plans are named — an archived plan the host can no longer buy must not be offered as the answer.
// ⚠ Pure: `npm run check-event-setup` runs it.
import { getFeatureValue } from '@em/utils';

export const ACTIVE_PLAN_STATUS = 100;

export interface PlanLike {
  planName?: string;
  planCode?: string;
  status?: number;
  planFeatures?: Array<{ featureValue?: string | null; feature?: { featureCode?: string | null; featureValueType?: string | null } | null }>;
}

/** The names of the active plans that include `planFeatureCode`, in the order given. Empty when none do. */
export const plansIncludingFeature = (plans: readonly PlanLike[], planFeatureCode: string | null | undefined): string[] => {
  if (!planFeatureCode) {
    return [];
  }

  return plans
    .filter((plan) => (plan.status ?? ACTIVE_PLAN_STATUS) === ACTIVE_PLAN_STATUS)
    .filter((plan) => {
      const pf = (plan.planFeatures ?? []).find((f) => f.feature?.featureCode === planFeatureCode);

      return !!pf && getFeatureValue(String(pf.featureValue ?? ''), pf.feature?.featureValueType ?? 'boolean') === true;
    })
    .map((plan) => plan.planName || plan.planCode || '')
    .filter(Boolean);
};

/** "Pro", "Pro or Business", "Pro, Business or Enterprise". */
export const joinPlanNames = (names: readonly string[], or = 'or'): string => {
  if (names.length <= 1) {
    return names[0] ?? '';
  }

  return `${names.slice(0, -1).join(', ')} ${or} ${names[names.length - 1]}`;
};

/**
 * ROADMAP #62 P6c — the premium features that STOP this event being published: the ones in use (the generated
 * `premiumFeaturesInUse`, grouped by plan feature) whose plan feature the account lacks. Feature codes, in catalog
 * order. The server refuses the same publish (`EventCore.premiumPublishRefusal`) — this is what lets the screen say so
 * BEFORE the host presses Publish, and offer to remove them.
 * ⚠ `lacks` is the app's own entitlement answer (it fails OPEN when the plan is unknown); the server has the last word.
 */
export const premiumFeaturesBlocking = (
  groups: readonly { planFeatureCode: string; features: string[] }[],
  lacks: (planFeatureCode: string) => boolean,
): string[] => groups.filter((group) => lacks(group.planFeatureCode)).flatMap((group) => group.features);
